import React, { createContext, useContext, useMemo } from 'react'

import { useQuery } from '@/api'
import { BRAINTREE_TOKEN_ENDPOINT, DEFAULT_CONTEXT_VALUES } from '@/constants'
import loggerContext from '@/utils/app-logger/context'

import { btClientConfigSelector, clientSelector } from './selector'
import type {
    BraintreeClientToken,
    BraintreeContextType,
    BraintreeProviderProps,
    BraintreeSelector,
    BraintreeState,
} from './types'
import { useBraintreeClient } from './useBraintreeClient'

const context = createContext<BraintreeContextType>({
    ...DEFAULT_CONTEXT_VALUES,
    state: {
        data: undefined,
        error: undefined,
        loading: false,
        btClientInstanceConfig: undefined,
    },
    selector: {},
})
const { Provider } = context

export const BraintreeClientTokenProvider: React.FC<BraintreeProviderProps> = ({ children, initialProps }) => {
    const {
        actions: { logError },
    } = useContext(loggerContext)

    const { data, error, loading } = useQuery<BraintreeClientToken>({
        enabled: true,
        endpoint: BRAINTREE_TOKEN_ENDPOINT,
        method: 'GET',
    })
    const { btClientInstanceConfig, isBtClientLoading, btClientInstanceError } = useBraintreeClient(data?.clientToken)

    if (error) {
        logError(new Error(error))
    }

    if (btClientInstanceError) {
        logError(new Error(JSON.stringify(btClientInstanceError)))
    }

    const contextValue = useMemo<BraintreeContextType>(() => {
        const state: BraintreeState = {
            data,
            error,
            loading: isBtClientLoading && loading,
            btClientInstanceConfig,
            ...initialProps,
        }

        const selector: BraintreeSelector = {
            clientToken: clientSelector(state),
            btGatewayConfigSelector: btClientConfigSelector(state),
        }

        return { state, selector }
    }, [data, btClientInstanceConfig, error, isBtClientLoading, loading, initialProps])

    return <Provider value={contextValue}>{children}</Provider>
}

export default context
